import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchFusionHistorySummary } from '../services/api';
import { makeSimilarityKey } from '../utils/fusionSimilarityKey';
import { buildCurrentEntry } from '../utils/fusionCostIndex';
import { waitForPhpPriorityIdle } from '../utils/phpRequestPriority';

export function useFusionCostIndex(rows, variant, { enabled = false } = {}) {
  const [summaries, setSummaries] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  const fingerprintMap = useMemo(() => {
    const map = new Map();
    if (!Array.isArray(rows)) return map;
    rows.forEach((row) => {
      const fp = makeSimilarityKey(row, variant);
      if (!fp) return;
      const current = buildCurrentEntry(row);
      if (current) {
        map.set(fp, current);
      }
    });
    return map;
  }, [rows, variant]);

  const fingerprintKey = useMemo(
    () => Array.from(fingerprintMap.keys()).sort().join('\u0001'),
    [fingerprintMap]
  );

  useEffect(() => {
    if (!enabled) {
      setSummaries({});
      setLoading(false);
      setError(null);
      return undefined;
    }

    const fingerprints = fingerprintKey ? fingerprintKey.split('\u0001') : [];
    if (fingerprints.length === 0) {
      setSummaries({});
      setLoading(false);
      setError(null);
      return undefined;
    }

    const requestId = ++requestIdRef.current;
    let cancelled = false;

    const load = async () => {
      setSummaries({});
      setLoading(true);
      setError(null);
      try {
        // Let gex.php run first on single-thread PHP dev server.
        await waitForPhpPriorityIdle();

        if (cancelled || requestId !== requestIdRef.current) return;

        const current = {};
        fingerprints.forEach((fp) => {
          if (fingerprintMap.has(fp)) {
            current[fp] = fingerprintMap.get(fp);
          }
        });
        const response = await fetchFusionHistorySummary(variant, fingerprints, current);
        if (cancelled || requestId !== requestIdRef.current) return;
        setSummaries(response?.summaries ?? {});
      } catch (err) {
        if (cancelled || requestId !== requestIdRef.current) return;
        setSummaries({});
        setError(err?.message || 'Failed to load fusion history');
      } finally {
        if (!cancelled && requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [enabled, fingerprintKey, fingerprintMap, variant]);

  const enrichedRows = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    return rows.map((row) => {
      const fp = makeSimilarityKey(row, variant);
      const summary = fp ? summaries[fp] : null;
      return {
        ...row,
        _similarityKey: fp,
        _costIndex: summary ?? null,
      };
    });
  }, [rows, summaries, variant]);

  return { enrichedRows, summaries, loading, error };
}
